/**
 * Lightweight React carousel.
 */

import React, { useCallback, useEffect, useState } from 'react'
import { cn } from '@/lib/utils'

/**
 * @param {object} props
 * @param {React.ReactNode[]} props.slides
 * @param {boolean} [props.autoPlay]
 * @param {number} [props.autoPlayDelay]
 * @param {boolean} [props.infinite]
 * @param {boolean} [props.showCounter]
 * @param {boolean} [props.showDots]
 * @param {'light' | 'dark'} [props.controls]
 * @param {string} [props.className]
 * @param {string} [props.slideClassName]
 */
export default function SimpleCarousel({
  slides = [],
  autoPlay = false,
  autoPlayDelay = 8000,
  infinite = true,
  showCounter = false,
  showDots = false,
  controls = 'light',
  className = '',
  slideClassName = '',
}) {
  const total = slides.length
  const [index, setIndex] = useState(0)

  const goPrev = useCallback(() => {
    setIndex((i) => {
      if (i <= 0) return infinite ? total - 1 : 0
      return i - 1
    })
  }, [infinite, total])

  const goNext = useCallback(() => {
    setIndex((i) => {
      if (i >= total - 1) return infinite ? 0 : total - 1
      return i + 1
    })
  }, [infinite, total])

  useEffect(() => {
    if (!autoPlay || total < 2) return undefined
    const timer = window.setInterval(goNext, autoPlayDelay)
    return () => window.clearInterval(timer)
  }, [autoPlay, autoPlayDelay, goNext, total])

  if (total === 0) return null

  const atStart = index === 0
  const atEnd = index === total - 1
  const prevDisabled = !infinite && atStart
  const nextDisabled = !infinite && atEnd

  const btnBase =
    controls === 'dark'
      ? 'bg-white/20 hover:bg-white/30 text-white'
      : 'bg-white/80 hover:bg-white text-gray-800 shadow-lg'

  return (
    <div className={cn('relative overflow-hidden', className)} role="region" aria-roledescription="carousel">
      {showCounter ? (
        <div className="absolute top-4 right-4 z-10 rounded-full bg-white/80 px-3 py-1 text-sm font-medium text-gray-800 shadow">
          {index + 1} / {total}
        </div>
      ) : null}

      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((slide, i) => (
            <div
              key={i}
              className={cn('w-full flex-shrink-0', slideClassName)}
              aria-hidden={i !== index}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      {total > 1 ? (
        <>
          <button
            type="button"
            onClick={goPrev}
            disabled={prevDisabled}
            aria-label="Previous slide"
            className={cn(
              'absolute top-1/2 -left-3 -translate-y-1/2 rounded-full p-3 transition-colors duration-200 disabled:pointer-events-none disabled:opacity-50',
              btnBase
            )}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={nextDisabled}
            aria-label="Next slide"
            className={cn(
              'absolute top-1/2 -right-3 -translate-y-1/2 rounded-full p-3 transition-colors duration-200 disabled:pointer-events-none disabled:opacity-50',
              btnBase
            )}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      ) : null}

      {showDots && total > 1 ? (
        <div className="absolute bottom-8 left-1/2 flex -translate-x-1/2 space-x-2">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === index}
              onClick={() => setIndex(i)}
              className={cn(
                'h-3 w-3 rounded-full border-2 transition-all duration-200',
                i === index
                  ? 'border-white bg-white'
                  : 'border-white/50 bg-transparent hover:border-white'
              )}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
